/**
 * WorkspaceRight - Sag panel
 *
 * Icerik:
 * - Uzman detaylari
 * - Bagiscilar listesi
 * - Token akisi (son islemler)
 * - Dal projeler
 */

import { ArrowRightLeft, GitBranch, Heart, Users } from 'lucide-react'
import FamilyChat from '../familyChat/FamilyChat'

interface Project {
  id: number
  assigned_experts: string[]
}

interface WorkspaceRightProps {
  project: Project
}

export default function WorkspaceRight({ project }: WorkspaceRightProps) {
  // Ornek veriler (gercekte API'den gelecek)
  const donors = [
    { id: 1, name: 'Kullanici 1', amount: 500, date: new Date().toISOString() },
    { id: 2, name: 'Kullanici 2', amount: 300, date: new Date().toISOString() },
  ]

  const tokenFlow = [
    { id: 1, type: 'donation', amount: 500, from: 'Kullanici 1', date: new Date().toISOString() },
    { id: 2, type: 'payment', amount: -50, to: 'Feynman', date: new Date().toISOString() },
    { id: 3, type: 'payment', amount: -30, to: 'Darwin', date: new Date().toISOString() },
  ]

  const branchProjects: any[] = []

  return (
    <div className="space-y-5">
      {/* Family Chat */}
      <div style={{ height: '500px' }}>
        <FamilyChat
          projectId={project.id}
          currentUserId={1}
          isDonor={false}
        />
      </div>

      {/* Uzman Detaylari */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title flex items-center gap-2">
            <Users size={20} />
            Uzman Detaylari
          </h3>
        </div>
        <div className="card-body">
          {project.assigned_experts.length > 0 ? (
            <div className="space-y-3">
              {project.assigned_experts.map((expertId) => (
                <div
                  key={expertId}
                  className="p-3 rounded"
                  style={{ background: 'var(--card-bg-secondary)' }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                      {expertId}
                    </span>
                    <span className="badge badge-success">Aktif</span>
                  </div>
                  <div className="text-sm space-y-1">
                    <div className="flex justify-between">
                      <span style={{ color: 'var(--text-secondary)' }}>Kalite:</span>
                      <span style={{ color: 'var(--text-primary)' }}>85/100</span>
                    </div>
                    <div className="flex justify-between">
                      <span style={{ color: 'var(--text-secondary)' }}>Cikti:</span>
                      <span style={{ color: 'var(--text-primary)' }}>3 bulgu</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: 'var(--text-secondary)' }} className="text-sm">
              Henuz uzman atanmadi
            </p>
          )}
        </div>
      </div>

      {/* Bagiscilar */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title flex items-center gap-2">
            <Heart size={20} />
            Bagiscilar
          </h3>
        </div>
        <div className="card-body">
          {donors.length > 0 ? (
            <div className="space-y-2">
              {donors.map((donor) => (
                <div
                  key={donor.id}
                  className="flex items-center justify-between p-2 rounded"
                  style={{ background: 'var(--card-bg-secondary)' }}
                >
                  <div>
                    <p className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                      {donor.name}
                    </p>
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                      {new Date(donor.date).toLocaleDateString('tr-TR')}
                    </p>
                  </div>
                  <span className="font-semibold" style={{ color: 'var(--success-color)' }}>
                    +{donor.amount} FLZ
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: 'var(--text-secondary)' }} className="text-sm">
              Henuz bagis yapilmadi
            </p>
          )}
        </div>
      </div>

      {/* Token Akisi */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title flex items-center gap-2">
            <ArrowRightLeft size={20} />
            Token Akisi
          </h3>
        </div>
        <div className="card-body">
          {tokenFlow.length > 0 ? (
            <div className="space-y-2">
              {tokenFlow.map((flow) => (
                <div
                  key={flow.id}
                  className="flex items-center justify-between p-2 rounded"
                  style={{ background: 'var(--card-bg-secondary)' }}
                >
                  <div>
                    <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                      {flow.type === 'donation' ? `${flow.from} bagis yapti` : `${flow.to} odeme`}
                    </p>
                    <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                      {new Date(flow.date).toLocaleString('tr-TR')}
                    </p>
                  </div>
                  <span
                    className="font-semibold"
                    style={{
                      color: flow.amount > 0 ? 'var(--success-color)' : 'var(--danger-color)'
                    }}
                  >
                    {flow.amount > 0 ? '+' : ''}{flow.amount} FLZ
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: 'var(--text-secondary)' }} className="text-sm">
              Henuz islem yok
            </p>
          )}
        </div>
      </div>

      {/* Dal Projeler */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title flex items-center gap-2">
            <GitBranch size={20} />
            Dal Projeler
          </h3>
        </div>
        <div className="card-body">
          {branchProjects.length > 0 ? (
            <div className="space-y-2">
              {branchProjects.map((branch: any) => (
                <div
                  key={branch.id}
                  className="p-2 rounded hover:bg-opacity-80 cursor-pointer transition"
                  style={{ background: 'var(--card-bg-secondary)' }}
                >
                  <p className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                    {branch.title}
                  </p>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    {branch.description}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: 'var(--text-secondary)' }} className="text-sm">
              Henuz dal proje yok
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
